/**
 * "Adressen ohne Land" (Adressmodell, WARTESCHLANGE Punkt 7, PR4 § 5 Punkt 5) — Sichtbarmachen
 * statt Backfill (Konzept-Dokument § 4): Migration 422 hat `address_country_code` additiv
 * angelegt, ohne Bestandsdaten rueckwirkend zu befuellen (Dubai-Fall — ein automatisches
 * Herleiten des Landes aus Freitext-Ort waere unzuverlaessig). Diese Seite listet genau die
 * Zeilen, bei denen Straße/PLZ/Ort vollstaendig gesetzt sind, aber das Land fehlt, ueber alle DREI
 * betroffenen Adress-Strukturen (Kunden-Stammadresse, Rechnungsadresse-Override, Lieferadresse-
 * Override) — Backend-Route `GET /api/customers/adressen-ohne-land` bereits live (PR2, Migration
 * 425).
 *
 * ## Warum diese Seite NICHT unter `pages/admin/` liegt
 *
 * Der Auftrag (wie auch das urspruengliche Konzept-Dokument) nennt `apps/web/src/pages/admin/` als
 * Vorbild. Geprueft: `pages/admin/*` ist der PLATTFORM-weite, mandantenUEBERGREIFENDE Admin-Bereich
 * (nur erreichbar auf dem Sonder-Host `maestro.<APP_BASE_DOMAIN>`, eigener `GET /api/admin/whoami`-
 * Gate fuer PLATTFORM-Admins — `docs/KONZEPT-ADMIN-BEREICH.md`). Die Backend-Route hier ist aber
 * MANDANTEN-gebunden (`withTenant`, `resolveTenantSlug` aus der normalen Subdomain) und prueft die
 * Rolle DES AUFRUFERS INNERHALB DIESES EINEN MANDANTEN (`ctx.role === "owner" || "admin"`) — ihr
 * eigener Kopfkommentar sagt woertlich "Rollen-Gate wie `/api/customers/duplicates`". Genau diese
 * Referenz-Flaeche (Dubletten-Review) liegt NICHT unter `pages/admin/`, sondern inline im
 * normalen Backoffice (`pages/customers.tsx`, gated auf `istInhaber` via `/api/me`). Eine
 * Platzierung unter `pages/admin/` waere fuer den eigentlichen Adressaten (den Owner/Admin EINES
 * Mandanten) schlicht nicht erreichbar (andere Domain, anderer Auth-Kontext) — deshalb hier bewusst
 * als eigene Seite im normalen Backoffice, mit demselben Rollen-Gate-Muster wie die
 * Dubletten-Kandidaten. Diese Abweichung ist eine begruendete Korrektur, kein Uebersehen — s.
 * Abschlussmeldung dieser Iteration.
 */
import { useApi } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import { vorgangPfad } from "../lib/pfade";
import type { Me } from "../ui/types";
import { Card, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader } from "../ui/components";
import { Link } from "react-router-dom";
import { anlassAnzeige, formatDate } from "../ui/format";

interface AdresseOhneLandKunde {
  id: string;
  name: string;
  company: string | null;
  addressStreet: string;
  addressZip: string;
  addressCity: string;
  createdAt: string;
}
interface AdresseOhneLandRechnung {
  id: string;
  number: string | null;
  occasion: string | null;
  eventDate: string | null;
  eventAddressStreet: string;
  eventAddressZip: string;
  eventAddressCity: string;
  customerName: string | null;
}
interface AdresseOhneLandLieferung {
  id: string;
  number: string | null;
  occasion: string | null;
  eventDate: string | null;
  deliveryAddressStreet: string;
  deliveryAddressZip: string;
  deliveryAddressCity: string;
  customerName: string | null;
}
interface AdressenOhneLandResponse {
  customers: AdresseOhneLandKunde[];
  eventAddresses: AdresseOhneLandRechnung[];
  deliveryAddresses: AdresseOhneLandLieferung[];
}

function vorgangLabel(number: string | null, occasion: string | null): string {
  return [number, anlassAnzeige(occasion)].filter(Boolean).join(" · ") || "Vorgang ohne Bezeichnung";
}

export default function AdressenOhneLandPage() {
  const meQ = useApi<Me>("/api/me");
  const membership = currentMembership(meQ.data);
  const istInhaber = membership?.role === "owner";
  const listQ = useApi<AdressenOhneLandResponse, { total: number }>(istInhaber ? "/api/customers/adressen-ohne-land" : null);

  if (meQ.loading) return <LoadingRows rows={4} />;

  if (!istInhaber) {
    return <EmptyState icon="lock" title="Kein Zugriff" hint="Diese Ansicht ist Inhabern vorbehalten." />;
  }

  const daten = listQ.data;
  const gesamt = listQ.zusatz?.total ?? (daten ? daten.customers.length + daten.eventAddresses.length + daten.deliveryAddresses.length : undefined);

  return (
    <>
      <PageHeader
        title="Adressen ohne Land"
        zurueck={{ label: "Kunden", to: "/kunden" }}
        subtitle={
          gesamt != null
            ? `${gesamt} Adresse${gesamt === 1 ? "" : "n"} mit vollständiger Straße/PLZ/Ort, aber ohne Land`
            : "Straße/PLZ/Ort vollständig, Land fehlt — über Kunden-, Rechnungs- und Lieferadressen hinweg."
        }
      />

      {listQ.loading && <LoadingRows rows={6} />}
      {listQ.error && <ErrorNote message={listQ.error} onRetry={listQ.reload} />}

      {daten && (
        <div className="space-y-5">
          <Card className="overflow-hidden">
            <div className="flex items-center gap-2.5 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
              <Icon name="person" className="text-[18px] text-primary" />
              <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Kunden-Stammadresse</span>
              <span className="rounded-full bg-primary/14 px-2 py-0.5 font-label-md text-[11px] font-bold text-primary">{daten.customers.length}</span>
            </div>
            {daten.customers.length === 0 ? (
              <p className="px-4 py-3 font-body-sm text-[12.5px] text-secondary-fixed-dim">Keine Kundenadresse ohne Land gefunden.</p>
            ) : (
              <ul>
                {daten.customers.map((k) => (
                  <li key={k.id} className="border-b border-outline-variant/10 px-4 py-3 last:border-b-0 dark:border-outline/10">
                    <Link to={`/kunden/${k.id}`} className="font-body-sm text-body-sm font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">
                      {k.company || k.name}
                    </Link>
                    <p className="mt-0.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      {k.addressStreet}, {k.addressZip} {k.addressCity}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="flex items-center gap-2.5 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
              <Icon name="receipt_long" className="text-[18px] text-primary" />
              <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Rechnungsadresse dieses Auftrags</span>
              <span className="rounded-full bg-primary/14 px-2 py-0.5 font-label-md text-[11px] font-bold text-primary">{daten.eventAddresses.length}</span>
            </div>
            {daten.eventAddresses.length === 0 ? (
              <p className="px-4 py-3 font-body-sm text-[12.5px] text-secondary-fixed-dim">Keine Rechnungsadresse-Override ohne Land gefunden.</p>
            ) : (
              <ul>
                {daten.eventAddresses.map((e) => (
                  <li key={e.id} className="border-b border-outline-variant/10 px-4 py-3 last:border-b-0 dark:border-outline/10">
                    <Link to={vorgangPfad(e.id)} className="font-body-sm text-body-sm font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">
                      {vorgangLabel(e.number, e.occasion)}
                    </Link>
                    <p className="mt-0.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      {e.customerName ? `${e.customerName} · ` : ""}
                      {e.eventAddressStreet}, {e.eventAddressZip} {e.eventAddressCity}
                      {e.eventDate ? ` · ${formatDate(e.eventDate)}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="overflow-hidden">
            <div className="flex items-center gap-2.5 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
              <Icon name="local_shipping" className="text-[18px] text-primary" />
              <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Lieferadresse</span>
              <span className="rounded-full bg-primary/14 px-2 py-0.5 font-label-md text-[11px] font-bold text-primary">{daten.deliveryAddresses.length}</span>
            </div>
            {daten.deliveryAddresses.length === 0 ? (
              <p className="px-4 py-3 font-body-sm text-[12.5px] text-secondary-fixed-dim">Keine Lieferadresse-Override ohne Land gefunden.</p>
            ) : (
              <ul>
                {daten.deliveryAddresses.map((e) => (
                  <li key={e.id} className="border-b border-outline-variant/10 px-4 py-3 last:border-b-0 dark:border-outline/10">
                    <Link to={vorgangPfad(e.id)} className="font-body-sm text-body-sm font-semibold text-on-background hover:text-primary dark:text-inverse-on-surface">
                      {vorgangLabel(e.number, e.occasion)}
                    </Link>
                    <p className="mt-0.5 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      {e.customerName ? `${e.customerName} · ` : ""}
                      {e.deliveryAddressStreet}, {e.deliveryAddressZip} {e.deliveryAddressCity}
                      {e.eventDate ? ` · ${formatDate(e.eventDate)}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
